import { Query } from '@apollo/react-components';
import React, { useState } from 'react';
import moment from 'moment';
import TextLoader from '../../common/components/TextLoader';
import { useMutation, useApolloClient } from '@apollo/react-hooks';
import {
  // EMAIL_STATS_RECEIVERS,
  GET_TIMESHEET_THRESHOLD,
  // GET_WEEKLY_REMINDER_EMAIL,
  UPDATE_SNAPSHOTS,
  GET_TIMESHEET_IDS_BY_END_DATE,
  GET_TIMESHEET_IDS_BY_DATE_RANGE,
} from '../queries';
import { Button, InputNumber, Input, Radio, Alert, Progress } from 'antd';
import { useLocation } from 'react-router-dom';
// import CronConfigContainer from './CronConfigContainer';
// import EmailStatsRecieverContainer from './EmailStatsRecieverContainer';
import TimesheetThresholdRuleContainer from './TimesheetThresholdRuleContainer';
import Card from '../../common/components/Card';
import WeekPicker from '../../common/components/WeekPicker';
import DateRangePicker from '../../common/components/DateRangePicker';
import { get } from 'lodash';
import UpdateBudgets from './UpdateBudgets';
import JobQueueMonitor from './JobQueueMonitor';

const MODE_DESCRIPTIONS = {
  weekEnding: 'Update all timesheet snapshots for the selected week.',
  timesheetId: 'Update the snapshot for a single timesheet.',
  projectId: 'Update snapshots for all timesheets in a project.',
  dateRange: 'Update all timesheet snapshots across a range of weeks.',
};

const CHUNK_SIZE = 5;

const Settings = () => {
  const location = useLocation();
  const showJobQueue = location.pathname.startsWith('/super-admin');
  const [mode, setMode] = useState('weekEnding');
  const [snapshotsUpdated, setSnapshotsUpdated] = useState(null);
  const [snapshotLimit, setSnapshotLimit] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [timesheetId, setTimesheetId] = useState(null);
  const [weekEndingDate, setWeekEndingDate] = useState(
    moment().endOf('isoWeek').format('YYYYMMDD'),
  );
  const [rangeStart, setRangeStart] = useState(moment().startOf('isoWeek').format('YYYYMMDD'));
  const [rangeEnd, setRangeEnd] = useState(moment().endOf('isoWeek').format('YYYYMMDD'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null); // { completed, total }
  const [updateSnapshots] = useMutation(UPDATE_SNAPSHOTS);
  const client = useApolloClient();

  const handleModeChange = (e) => {
    setMode(e.target.value);
    setSnapshotsUpdated(null);
    setError(null);
    setProgress(null);
  };

  const isButtonDisabled =
    (mode === 'timesheetId' && !timesheetId) ||
    (mode === 'projectId' && !projectId);

  const handleUpdateSnapshots = async () => {
    setLoading(true);
    setSnapshotsUpdated(null);
    setError(null);
    setProgress(null);
    try {
      if (mode === 'weekEnding' || mode === 'dateRange') {
        // Chunked processing: fetch IDs first, then send in batches of CHUNK_SIZE
        let allIds;
        if (mode === 'weekEnding') {
          const { data: idsData } = await client.query({
            query: GET_TIMESHEET_IDS_BY_END_DATE,
            variables: { endDate: weekEndingDate },
            fetchPolicy: 'network-only',
          });
          allIds = idsData?.timesheetIdsByEndDate || [];
        } else {
          const { data: idsData } = await client.query({
            query: GET_TIMESHEET_IDS_BY_DATE_RANGE,
            variables: { startDate: rangeStart, endDate: rangeEnd },
            fetchPolicy: 'network-only',
          });
          allIds = idsData?.timesheetIdsByEndDateRange || [];
        }

        if (allIds.length === 0) {
          setSnapshotsUpdated(0);
          return;
        }

        let totalUpdated = 0;
        setProgress({ completed: 0, total: allIds.length });

        for (let i = 0; i < allIds.length; i += CHUNK_SIZE) {
          const chunk = allIds.slice(i, i + CHUNK_SIZE);
          const { data } = await updateSnapshots({
            variables: { timesheetIds: chunk },
          });
          totalUpdated += data.updateTimesheetSnapshots;
          setProgress({ completed: totalUpdated, total: allIds.length });
        }
        setSnapshotsUpdated(totalUpdated);
      } else {
        const variables = { limit: snapshotLimit || 10 };
        if (mode === 'timesheetId') {
          variables.timesheetId = timesheetId;
        } else if (mode === 'projectId') {
          variables.projectId = projectId;
        }
        const { data } = await updateSnapshots({ variables });
        setSnapshotsUpdated(data.updateTimesheetSnapshots);
      }
    } catch (err) {
      setError(err.message || 'An error occurred while updating snapshots.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div>
      <div className="flex gap-2">
        <Card title="Timesheet Settings" border padded={false} floating className="w-1/2">
          <Query query={GET_TIMESHEET_THRESHOLD}>
            {({ loading, error, data }) => {
              if (error) throw new Error(error);
              if (loading) {
                return <TextLoader text="Loading" />;
              }
              const thresholdDetails = get(data, 'timesheetThreshold');
              return <TimesheetThresholdRuleContainer thresholdDetails={thresholdDetails} />;
            }}
          </Query>
        </Card>
        <div className="w-1/2">
          <Card floating title="Snapshots" border padded>
            <div className="flex flex-col gap-3">
              <Radio.Group
                value={mode}
                onChange={handleModeChange}
                buttonStyle="solid"
                size="small"
                className="w-full"
              >
                <Radio.Button value="weekEnding">By Week</Radio.Button>
                <Radio.Button value="timesheetId">By Timesheet</Radio.Button>
                <Radio.Button value="projectId">By Project</Radio.Button>
                <Radio.Button value="dateRange">By Date Range</Radio.Button>
              </Radio.Group>

              <div className="text-xs text-gray-500">{MODE_DESCRIPTIONS[mode]}</div>

              {mode === 'weekEnding' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Week Ending</label>
                  <WeekPicker
                    endDate={weekEndingDate}
                    onDateChange={setWeekEndingDate}
                    loading={loading}
                  />
                </div>
              )}

              {mode === 'timesheetId' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Timesheet ID</label>
                  <Input
                    className="w-full"
                    placeholder="e.g. 60f7b2a..."
                    value={timesheetId || ''}
                    onChange={e => setTimesheetId(e.target.value)}
                  />
                </div>
              )}

              {mode === 'projectId' && (
                <>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Project ID</label>
                    <Input
                      className="w-full"
                      placeholder="e.g. 60f7b2a..."
                      value={projectId || ''}
                      onChange={e => setProjectId(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Limit <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <InputNumber
                      className="w-full"
                      placeholder="Default: 10"
                      value={snapshotLimit}
                      onChange={value => setSnapshotLimit(value)}
                      min={1}
                    />
                  </div>
                </>
              )}

              {mode === 'dateRange' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Date Range</label>
                  <DateRangePicker
                    startDate={rangeStart}
                    endDate={rangeEnd}
                    loading={loading}
                    handleDateChange={range => {
                      setRangeStart(moment(range[0]).format('YYYYMMDD'));
                      setRangeEnd(moment(range[1]).format('YYYYMMDD'));
                    }}
                  />
                </div>
              )}

              <Button
                loading={loading}
                disabled={isButtonDisabled}
                onClick={handleUpdateSnapshots}
              >
                Update Snapshots
              </Button>

              {loading && progress && (
                <div>
                  <div className="mb-1 text-xs text-gray-500">
                    Processing {progress.completed} of {progress.total} timesheets...
                  </div>
                  <Progress
                    percent={Math.round((progress.completed / progress.total) * 100)}
                    size="small"
                    status="active"
                  />
                </div>
              )}

              {snapshotsUpdated !== null && (
                <Alert
                  type="success"
                  showIcon
                  message={`${snapshotsUpdated} snapshot${snapshotsUpdated === 1 ? '' : 's'} updated.`}
                  closable
                  onClose={() => setSnapshotsUpdated(null)}
                />
              )}
              {error && (
                <Alert
                  type="error"
                  showIcon
                  message="Snapshot update failed"
                  description={error}
                  closable
                  onClose={() => setError(null)}
                />
              )}
            </div>
          </Card>
          <Card floating title="Budgets" className="w-full" border padded>
            <UpdateBudgets />
          </Card>
          {showJobQueue && (
            <Card floating title="Job Queue" className="w-full" border padded>
              <JobQueueMonitor />
            </Card>
          )}
        </div>
      </div>
      {/* <Card floating bodyBackground={null} title="Reminder Emails">
        <div className="flex w-full flex-wrap">
          <div className="w-full">
            <Query query={EMAIL_STATS_RECEIVERS}>
              {({ loading, error, data }) => {
                if (error) throw new Error(error);
                if (loading) {
                  return <TextLoader text="Loading" />;
                }
                const emailStatsRecievers = get(data, 'admin.emailStatsRecievers');
                const users = get(data, 'admin.users');
                return (
                  <EmailStatsRecieverContainer
                    users={users}
                    emailStatsRecievers={emailStatsRecievers}
                  />
                );
              }}
            </Query>
          </div>
          <div className="w-full">
            <Query query={GET_WEEKLY_REMINDER_EMAIL}>
              {({ loading, error, data }) => {
                if (error) throw new Error(error);
                if (loading) {
                  return <TextLoader text="Loading" />;
                }
                const weeklyReminderEmailJob = get(data, 'weeklyReminderEmailJob');
                return <CronConfigContainer weeklyReminderEmailJob={weeklyReminderEmailJob} />;
              }}
            </Query>
          </div>
        </div>
      </Card> */}
    </div>
  );
};

export default Settings;
